import {
  BRIDGE_GLOBAL,
  BRIDGE_TIMEOUT_MS,
  type AccountCapabilities,
  type AccountFailure,
  type AccountLinkedProvider,
  type AccountProfile,
  type AccountReply,
  type AccountCall,
} from '@cottonray/auth-contract'

/**
 * 다리의 **웹 쪽 절반**. 페이지는 세션을 갖지 않고, 필요한 것을 앱에 부탁한다.
 *
 * 왜 토큰을 받지 않는지는 `@cottonray/auth-contract` 머리말에 있다. 요약하면: 토큰을
 * 페이지에 넘기면 realm 여섯·서비스 넷에 CORS 를 열어야 하고 갱신 토큰까지 따라 나간다.
 * 이 구조에서는 페이지에 **유출할 세션 자체가 없다**.
 *
 * ## 앱 밖에서 열면
 *
 * `window.ReactNativeWebView` 가 없으면 여기는 아무것도 못 한다. 그건 오류가 아니라
 * "이 화면은 앱에서 연다"는 안내를 띄울 상황이다 ([inApp]).
 */

interface Pending {
  resolve: (reply: AccountReply) => void
  timer: ReturnType<typeof setTimeout>
}

const pending = new Map<string, Pending>()
let counter = 0

interface ReactNativeWebViewBridge {
  postMessage: (message: string) => void
}

function host(): ReactNativeWebViewBridge | undefined {
  return (window as unknown as { ReactNativeWebView?: ReactNativeWebViewBridge }).ReactNativeWebView
}

/** 앱 안 웹뷰에 담겨 있나. 아니면 이 페이지는 할 수 있는 것이 없다 */
export function inApp(): boolean {
  return host() !== undefined
}

/**
 * 앱이 답을 꽂을 자리. **모듈이 로드될 때 바로 심는다** — 앱은 답을 아무 때나 보내는 것이
 * 아니라 우리가 부탁한 뒤에만 보내지만, 그 사이에 전역이 없으면 답이 통째로 사라진다.
 */
function install(): void {
  const target = window as unknown as Record<string, unknown>
  if (target[BRIDGE_GLOBAL] !== undefined) return
  target[BRIDGE_GLOBAL] = {
    resolve(raw: string) {
      let reply: AccountReply
      try {
        reply = JSON.parse(raw) as AccountReply
      } catch {
        return
      }
      const waiting = pending.get(reply.id)
      if (waiting === undefined) return
      pending.delete(reply.id)
      clearTimeout(waiting.timer)
      waiting.resolve(reply)
    },
  }
}

install()

/**
 * 앱에 한 가지를 부탁하고 답을 기다린다.
 *
 * **끝내 답이 없어도 매달려 있지 않는다** — 앱이 어떤 이유로 답을 못 보내면 화면이 영영
 * 회전만 하게 된다. 상한을 넘기면 "연결 문제"로 처리해 사용자가 다시 시도할 수 있게 한다.
 */
export async function call(request: AccountCall): Promise<AccountReply> {
  const bridge = host()
  const id = `r${(counter += 1)}`
  if (bridge === undefined) return { id, ok: false, reason: 'unsupported' }

  return new Promise<AccountReply>((resolve) => {
    const timer = setTimeout(() => {
      pending.delete(id)
      resolve({ id, ok: false, reason: 'network' })
    }, BRIDGE_TIMEOUT_MS)
    pending.set(id, { resolve, timer })
    bridge.postMessage(JSON.stringify({ ...request, id }))
  })
}

/** 값을 기대하는 호출 — 실패하면 사유를 그대로 준다 */
export type Result<T> = { ok: true; value: T } | { ok: false; reason: AccountFailure }

async function ask<T>(request: AccountCall): Promise<Result<T>> {
  const reply = await call(request)
  return reply.ok ? { ok: true, value: reply.value as T } : { ok: false, reason: reply.reason }
}

export const accountBridge = {
  profile: () => ask<AccountProfile>({ method: 'profile' }),
  capabilities: () => ask<AccountCapabilities>({ method: 'capabilities' }),
  linkedAccounts: () => ask<AccountLinkedProvider[]>({ method: 'linkedAccounts' }),
  /** data URL 이거나 null (사진이 없거나 못 읽었다) */
  avatar: () => ask<string | null>({ method: 'avatar' }),
  updateProfile: (displayName: string, email: string) =>
    ask<null>({ method: 'updateProfile', displayName, email }),
  changePassword: (currentPassword: string, newPassword: string) =>
    ask<null>({ method: 'changePassword', currentPassword, newPassword }),
  /** 앱의 카메라·앨범을 연다. 취소하면 `{ok:true, value:null}` — 취소는 오류가 아니다 */
  pickPhoto: (source: 'camera' | 'library') => ask<string | null>({ method: 'pickPhoto', source }),
  deletePhoto: () => ask<null>({ method: 'deletePhoto' }),
  deleteAccount: () => ask<null>({ method: 'deleteAccount' }),
  logout: () => ask<null>({ method: 'logout' }),
  /** 앱에게 이 화면을 닫아 달라고 한다 */
  close: () => ask<null>({ method: 'close' }),
}
